import { useEffect, useState, useMemo } from "react";
import { Plus, Trash2, Send, MessageSquare, Loader2 } from "lucide-react";
import { api, formatErr, cx } from "../lib/api";
import { useFx } from "../lib/fx";
import { useI18n } from "../lib/i18n";
import ChatDrawer from "./ChatDrawer";

const blankItem = () => ({
  service_id: "",
  title: "",
  provider_name: "",
  description: "",
  start_date: "",
  end_date: "",
  guests: "",
  price: "",
  currency: "EUR",
  deposit_percent: 30,
});

/**
 * Admin offer builder for a single custom trip request.
 * Props: trip — { id, ... }, services — array of admin services, onChange()
 */
function OfferBuilder({ trip, services, onSent }) {
  const { t } = useI18n();
  const { format } = useFx();
  const [items, setItems] = useState([blankItem()]);
  const [title, setTitle] = useState("Custom trip — Meet & Mint");
  const [summary, setSummary] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Pre-load existing offer if any
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/custom-trips/${trip.id}`);
        if (data.offer && data.offer.status === "pending") {
          setTitle(data.offer.title);
          setSummary(data.offer.summary || "");
          setNote(data.offer.note_to_user || "");
          setItems(data.offer.items.map((i) => ({
            service_id: i.service_id || "",
            title: i.title,
            provider_name: i.provider_name || "",
            description: i.description || "",
            start_date: i.start_date || "",
            end_date: i.end_date || "",
            guests: i.guests || "",
            price: i.price ?? "",
            currency: i.currency || "EUR",
            deposit_percent: i.deposit_percent ?? 30,
          })));
        }
      } catch { /* ignore */ }
    })();
  }, [trip.id]);

  const setItem = (idx, k, v) => {
    setItems((arr) => arr.map((it, i) => i === idx ? { ...it, [k]: v } : it));
  };

  // When admin picks a service, prefill the line
  const onServicePick = (idx, sid) => {
    setItem(idx, "service_id", sid);
    if (!sid) return;
    const svc = services.find((s) => s.id === sid);
    if (svc) {
      setItem(idx, "title", svc.title);
      setItem(idx, "price", svc.base_price);
      setItem(idx, "currency", svc.currency);
      setItem(idx, "deposit_percent", svc.deposit_percent ?? 30);
    }
  };

  const total = useMemo(
    () => items.reduce((s, i) => s + (Number(i.price) || 0), 0),
    [items],
  );
  const ccy = items[0]?.currency || "EUR";
  const deposit = useMemo(
    () => items.reduce((s, i) => s + (Number(i.price) || 0) * (Number(i.deposit_percent) || 0) / 100, 0),
    [items],
  );

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const payload = {
        title: title.trim(),
        summary: summary.trim() || null,
        note_to_user: note.trim() || null,
        items: items.map((i) => ({
          service_id: i.service_id || null,
          title: i.title.trim(),
          provider_name: i.provider_name.trim() || null,
          description: i.description.trim() || null,
          start_date: i.start_date || null,
          end_date: i.end_date || null,
          guests: i.guests ? Number(i.guests) : null,
          price: Number(i.price),
          currency: i.currency,
          deposit_percent: Number(i.deposit_percent),
        })),
      };
      await api.post(`/custom-trips/${trip.id}/offers`, payload);
      onSent?.();
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="luxe-card p-5 mt-3" data-testid={`offer-builder-${trip.id}`}>
      <div className="eyebrow mb-2 text-teal">Build offer</div>

      <div className="grid md:grid-cols-2 gap-3 mb-4">
        <label className="block">
          <span className="label-tiny block mb-1.5">Offer title</span>
          <input className="input-bare" value={title} onChange={(e) => setTitle(e.target.value)} data-testid={`ob-title-${trip.id}`}/>
        </label>
        <label className="block">
          <span className="label-tiny block mb-1.5">Summary (optional)</span>
          <input className="input-bare" value={summary} onChange={(e) => setSummary(e.target.value)} data-testid={`ob-summary-${trip.id}`}/>
        </label>
      </div>

      <div className="space-y-2 mb-3">
        {items.map((it, idx) => (
          <div key={idx} className="luxe-card p-3 border hairline" data-testid={`ob-item-${trip.id}-${idx}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="label-tiny">Item #{idx + 1}</span>
              {items.length > 1 && (
                <button type="button" onClick={() => setItems((arr) => arr.filter((_, i) => i !== idx))}
                        className="text-rose-300/80 hover:text-rose-300 text-xs"
                        data-testid={`ob-remove-${trip.id}-${idx}`}>
                  <Trash2 size={12}/>
                </button>
              )}
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              <label className="block md:col-span-2">
                <span className="label-tiny block mb-1">Link to service (optional)</span>
                <select className="input-bare" value={it.service_id} onChange={(e) => onServicePick(idx, e.target.value)} data-testid={`ob-svc-${trip.id}-${idx}`}>
                  <option value="">— Free-form line —</option>
                  {services.filter(s => s.status === "published").map((s) => (
                    <option key={s.id} value={s.id}>{s.title} ({s.currency} {s.base_price})</option>
                  ))}
                </select>
              </label>
              <label className="block md:col-span-2">
                <span className="label-tiny block mb-1">Title</span>
                <input className="input-bare" value={it.title} onChange={(e) => setItem(idx, "title", e.target.value)} required data-testid={`ob-title-i-${trip.id}-${idx}`}/>
              </label>
              <label className="block">
                <span className="label-tiny block mb-1">Provider name (free-form)</span>
                <input className="input-bare" value={it.provider_name} onChange={(e) => setItem(idx, "provider_name", e.target.value)}/>
              </label>
              <label className="block">
                <span className="label-tiny block mb-1">Guests</span>
                <input type="number" min="1" className="input-bare" value={it.guests} onChange={(e) => setItem(idx, "guests", e.target.value)}/>
              </label>
              <label className="block">
                <span className="label-tiny block mb-1">Start</span>
                <input type="date" className="input-bare" value={it.start_date} onChange={(e) => setItem(idx, "start_date", e.target.value)}/>
              </label>
              <label className="block">
                <span className="label-tiny block mb-1">End</span>
                <input type="date" className="input-bare" value={it.end_date} onChange={(e) => setItem(idx, "end_date", e.target.value)}/>
              </label>
              <label className="block">
                <span className="label-tiny block mb-1">Price</span>
                <input type="number" min="0" step="0.01" className="input-bare" value={it.price} onChange={(e) => setItem(idx, "price", e.target.value)} required data-testid={`ob-price-${trip.id}-${idx}`}/>
              </label>
              <label className="block">
                <span className="label-tiny block mb-1">Deposit %</span>
                <input type="number" min="0" max="100" className="input-bare" value={it.deposit_percent} onChange={(e) => setItem(idx, "deposit_percent", e.target.value)}/>
              </label>
              <label className="block md:col-span-2">
                <span className="label-tiny block mb-1">Description</span>
                <textarea rows={2} className="input-bare resize-y" value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)}/>
              </label>
            </div>
          </div>
        ))}
      </div>

      <button type="button" onClick={() => setItems((a) => [...a, blankItem()])}
              className="btn btn-ghost text-[12px]" data-testid={`ob-add-item-${trip.id}`}>
        <Plus size={12}/> Add another item
      </button>

      <label className="block mt-4">
        <span className="label-tiny block mb-1.5">Note to guest (optional)</span>
        <textarea rows={2} className="input-bare resize-y" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`ob-note-${trip.id}`}/>
      </label>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-3 border-t hairline">
        <div className="text-sm">
          <span className="label-tiny">Total</span>
          <div className="font-serif-display text-2xl">{format(total, ccy)}</div>
          <div className="text-xs text-muted">Deposit: {format(deposit, ccy)}</div>
        </div>
        <button type="button" onClick={submit} disabled={busy} className="btn btn-primary" data-testid={`ob-submit-${trip.id}`}>
          {busy ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>} Send offer to guest
        </button>
      </div>

      {error && <div className="text-rose-300 text-sm mt-3">{error}</div>}
    </div>
  );
}

export default function AdminTripsPanel({ user }) {
  const { t } = useI18n();
  const { format } = useFx();
  const [trips, setTrips] = useState([]);
  const [services, setServices] = useState([]);
  const [error, setError] = useState(null);
  const [openTripId, setOpenTripId] = useState(null);
  const [chatUserId, setChatUserId] = useState(null);
  const [tripOffers, setTripOffers] = useState({}); // tripId -> latest offer

  const reload = async () => {
    try {
      const [tt, sv] = await Promise.all([
        api.get("/custom-trips"),
        api.get("/admin/services"),
      ]);
      setTrips(tt.data.items || []);
      setServices(sv.data.items || []);
      const offers = {};
      for (const tr of tt.data.items || []) {
        try {
          const { data } = await api.get(`/custom-trips/${tr.id}`);
          if (data.offer) offers[tr.id] = data.offer;
        } catch { /* ignore */ }
      }
      setTripOffers(offers);
    } catch (e) { setError(formatErr(e)); }
  };

  useEffect(() => { reload(); }, []);

  return (
    <div>
      <div className="mb-5">
        <div className="eyebrow">Custom trips</div>
        <h2 className="font-serif-display text-3xl mt-1">Trip requests</h2>
        <p className="text-sm text-muted mt-1 max-w-xl">
          Build a multi-service offer for each request. Once accepted, bookings auto-create for any items linked to a real service.
        </p>
      </div>

      {error && <div className="text-rose-300 text-sm mb-4">{error}</div>}

      <div className="space-y-3">
        {trips.length === 0 && <div className="luxe-card p-6 text-sm text-muted">No custom trip requests yet.</div>}
        {trips.map((tr) => {
          const isOpen = openTripId === tr.id;
          const o = tripOffers[tr.id];
          return (
            <div key={tr.id} className="luxe-card p-5" data-testid={`adt-${tr.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={cx("chip", o?.status === "accepted" ? "success" : tr.status === "offer_sent" ? "info" : "warning")}>
                      {tr.status}
                    </span>
                    <span className="text-xs text-muted font-mono">#{tr.id.slice(0,8)}</span>
                  </div>
                  <div className="font-serif-display text-xl">{(tr.destinations || []).join(" · ") || "Custom"}</div>
                  <div className="text-xs text-muted mt-1">
                    {tr.guests} guests · {tr.start_date || "flexible"} · {(tr.budget_min || 0)}–{(tr.budget_max || 0)} {tr.currency}
                    {tr.user_email && <span> · <span className="font-mono">{tr.user_email}</span></span>}
                  </div>
                  {tr.raw_user_input && <div className="text-sm text-muted mt-2 italic line-clamp-2">"{tr.raw_user_input}"</div>}
                  {o && (
                    <div className="mt-2 text-xs">
                      <span className={cx("chip", o.status === "accepted" ? "success" : o.status === "declined" ? "danger" : "info")}>{o.status}</span>{" "}
                      <span className="text-muted">{o.title} · {format(o.total_price, o.currency)}</span>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => setOpenTripId(isOpen ? null : tr.id)}
                          className="btn btn-primary text-[12px]" data-testid={`adt-build-${tr.id}`}>
                    {isOpen ? "Close" : (o?.status === "pending" ? "Edit offer" : "Build offer")}
                  </button>
                  {tr.user_id && (
                    <button onClick={() => setChatUserId(tr.user_id)}
                            className="btn btn-ghost text-[12px]" data-testid={`adt-msg-${tr.id}`}>
                      <MessageSquare size={12}/> Message
                    </button>
                  )}
                </div>
              </div>

              {isOpen && (
                <OfferBuilder
                  trip={tr}
                  services={services}
                  onSent={() => { setOpenTripId(null); reload(); }}
                />
              )}
            </div>
          );
        })}
      </div>

      {chatUserId && (
        <ChatDrawer kind="concierge" counterpartyUserId={chatUserId} onClose={() => setChatUserId(null)} />
      )}
    </div>
  );
}
